import React, { useState } from 'react';
import { X, Loader2, AlertTriangle } from 'lucide-react';
import crm from './api';

// Creating an employee is a single call now. It used to need two — an external
// server to mint the card slug, then the CRM to create the login — which left a
// half-made employee behind whenever the second call failed. The CRM generates
// the slug itself, so the login and the card are created together or not at all.
// Passing `employee` switches the form to editing that person. Email is their
// login and stays fixed; password becomes optional and only changes when typed.
export default function AddEmployeeModal({ onClose, onCreated, employee }) {
  const editing = !!employee;
  const [f, setF] = useState({
    name: employee?.name || '',
    email: employee?.email || '',
    whatsapp: employee?.whatsapp || '',
    designation: employee?.designation || '',
    // No default. A shared starter password means every new login shares it,
    // and the string ends up in the shipped bundle for anyone to read.
    password: '',
    company: employee?.company || '',
    linkedin: employee?.linkedin || '',
    cardTheme: employee?.cardTheme || '',
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [step, setStep] = useState('');

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  async function submit(e) {
    e.preventDefault();
    setErr('');
    if (!f.name.trim() || !f.email.trim()) return setErr('Name and email are both required.');
    // When editing, a blank password means "leave it alone" rather than "too short".
    if ((!editing || f.password) && f.password.length < 6) return setErr('Password must be at least 6 characters.');
    setBusy(true);

    try {
      setStep(editing ? 'Saving…' : 'Creating employee & card…');
      const body = {
        name: f.name.trim(),
        designation: f.designation.trim(),
        // Blank means "use the company default" on every one of these — the server
        // stores null and the card falls back, so leaving them empty is safe.
        whatsapp: f.whatsapp.trim(),
        company: f.company.trim(),
        linkedin: f.linkedin.trim(),
        cardTheme: f.cardTheme,
      };
      if (f.password) body.password = f.password;

      const { data } = editing
        ? await crm.put(`/employees/${employee.id}`, body)
        : await crm.post('/employees', { ...body, email: f.email.trim().toLowerCase() });
      if (!data?.employee?.id) throw new Error(data?.error || 'The server did not return an employee.');

      onCreated(data.employee);
    } catch (e2) {
      const msg = e2.response?.data?.error || e2.response?.data?.message || e2.message;
      setErr(`Could not ${editing ? 'save' : 'create'} employee: ${msg}`);
    } finally {
      setBusy(false); setStep('');
    }
  }

  const field = 'w-full text-[13.5px] rounded-lg px-3 py-2 outline-none border border-[var(--border)] bg-[var(--soft)] text-[var(--text)]';
  const label = 'block text-[11px] font-bold uppercase tracking-wide text-[var(--muted)] mb-1.5';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(8,14,11,.55)' }}>
      <div className="w-full max-w-md rounded-2xl border border-[var(--border)] bg-[var(--card)] card-lux overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-[var(--border)]">
          <h3 className="font-extrabold text-[var(--text)] text-[16px]">{editing ? 'Edit employee' : 'Add employee'}</h3>
          <button onClick={onClose} aria-label="Close" className="text-[var(--muted)] hover:text-[var(--text)]"><X size={18} /></button>
        </div>

        <form onSubmit={submit} className="p-5 space-y-3.5">
          <div className="flex gap-2 text-[12px] rounded-lg p-2.5" style={{ background: 'var(--acc-soft)', color: 'var(--muted)' }}>
            <AlertTriangle size={15} className="shrink-0 mt-0.5" style={{ color: 'var(--acc2)' }} />
            <span>{editing
              ? 'Changes appear on their live card and microsite straight away.'
              : 'Creates their CRM login and a live digital card in one step.'}</span>
          </div>

          <div>
            <label className={label} htmlFor="ae-name">Full name</label>
            <input id="ae-name" className={field} value={f.name} onChange={set('name')} placeholder="Ramesh Kumar" autoFocus />
          </div>
          <div>
            <label className={label} htmlFor="ae-email">Email (this is their login)</label>
            <input id="ae-email" type="email" className={field} value={f.email} onChange={set('email')}
              disabled={editing} placeholder="ramesh.dfc@dfcindia.in"
              style={editing ? { opacity: .6, cursor: 'not-allowed' } : undefined} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={label} htmlFor="ae-desig">Designation</label>
              <input id="ae-desig" className={field} value={f.designation} onChange={set('designation')} placeholder="Sales Manager" />
            </div>
            <div>
              <label className={label} htmlFor="ae-wa">WhatsApp number</label>
              <input id="ae-wa" className={field} value={f.whatsapp} onChange={set('whatsapp')} placeholder="9654492529" />
            </div>
          </div>
          <p className="text-[11.5px] -mt-1.5" style={{ color: 'var(--muted)' }}>
            Enquiries from this employee's card open a chat on their number. Leave it blank and they
            go to the shared company line instead.
          </p>
          <div>
            <label className={label} htmlFor="ae-pw">{editing ? 'New password (blank keeps the current one)' : 'Password'}</label>
            <input id="ae-pw" className={field} value={f.password} onChange={set('password')}
              placeholder={editing ? 'unchanged' : ''} />
          </div>

          <details className="rounded-lg border border-[var(--border)] px-3 py-2">
            <summary className="text-[12px] font-bold cursor-pointer text-[var(--muted)]">
              Card branding — optional
            </summary>
            <div className="space-y-3.5 pt-3">
              <div>
                <label className={label} htmlFor="ae-co">Company on the card</label>
                <input id="ae-co" className={field} value={f.company} onChange={set('company')} placeholder="TECGSOFT" />
              </div>
              <div>
                <label className={label} htmlFor="ae-li">Personal LinkedIn</label>
                <input id="ae-li" className={field} value={f.linkedin} onChange={set('linkedin')} placeholder="Blank uses the DFC company page" />
              </div>
              <div>
                <label className={label} htmlFor="ae-theme">Card colour</label>
                <select id="ae-theme" className={field} value={f.cardTheme} onChange={set('cardTheme')}>
                  <option value="">Green (house style)</option>
                  <option value="charcoal">Charcoal</option>
                </select>
              </div>
            </div>
          </details>

          {err && <div className="text-[12.5px] rounded-lg p-2.5" style={{ background: '#fef2f2', color: '#991b1b' }}>{err}</div>}

          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={onClose} className="text-sm font-semibold text-[var(--muted)] px-3.5 py-2 rounded-lg border border-[var(--border)]">Cancel</button>
            <button type="submit" disabled={busy} className="flex items-center gap-2 acc-bg text-white text-sm font-bold px-4 py-2 rounded-lg disabled:opacity-50">
              {busy && <Loader2 size={14} className="animate-spin" />}
              {busy ? step || 'Saving…' : (editing ? 'Save changes' : 'Create employee')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
